import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Input, InputGroup, InputGroupText, Row } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { api } from '../../utils';
import { getDownloadFilename, triggerBlobDownload } from '../../utils/download';
import lodash from 'lodash';

const DEFAULT_SIZE_PER_PAGE = 100;
const SIZE_PER_PAGE_LIST = [
  { text: '50', value: 50 },
  { text: '100', value: 100 },
  { text: '250', value: 250 },
];

const getPaginationOptions = () => ({
  sizePerPage: DEFAULT_SIZE_PER_PAGE,
  sizePerPageList: SIZE_PER_PAGE_LIST,
  hideSizePerPage: false,
  showTotal: true,
});

const buildDomainParams = (search, topic, rGte, tGte, trGte, adrGte, atrGte, ardGte) => {
  const params = {};
  if (search) params.search = search;
  if (topic) params.topic = topic;
  if (rGte) params.rating_gte = rGte;
  if (tGte) params.trust_gte = tGte;
  if (trGte) params.traffic_gte = trGte;
  if (adrGte) params.ahrefs_dr_gte = adrGte;
  if (atrGte) params.ahrefs_org_traffic_gte = atrGte;
  if (ardGte) params.ahrefs_refdomains_gte = ardGte;
  return params;
};

const DomainList = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingExport, setDownloadingExport] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [minRating, setMinRating] = useState('');
  const [minTrust, setMinTrust] = useState('');
  const [minTraffic, setMinTraffic] = useState('');
  const [minAhrefsDr, setMinAhrefsDr] = useState('');
  const [minAhrefsTraffic, setMinAhrefsTraffic] = useState('');
  const [minAhrefsRefdomains, setMinAhrefsRefdomains] = useState('');
  const [refreshingAhrefs, setRefreshingAhrefs] = useState(false);
  const [refreshingSemrush, setRefreshingSemrush] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState([]);

  const fileInputRef = useRef(null);

  const debouncedFetch = useCallback(
    lodash.debounce((search, topic, rGte, tGte, trGte, adrGte, atrGte, ardGte) => {
      fetchDomains(search, topic, rGte, tGte, trGte, adrGte, atrGte, ardGte);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedFetch(
      searchQuery,
      topicFilter,
      minRating,
      minTrust,
      minTraffic,
      minAhrefsDr,
      minAhrefsTraffic,
      minAhrefsRefdomains
    );
  }, [
    searchQuery,
    topicFilter,
    minRating,
    minTrust,
    minTraffic,
    minAhrefsDr,
    minAhrefsTraffic,
    minAhrefsRefdomains,
    debouncedFetch,
  ]);

  const fetchDomains = async (
    search = '',
    topic = '',
    rGte = '',
    tGte = '',
    trGte = '',
    adrGte = '',
    atrGte = '',
    ardGte = ''
  ) => {
    try {
      setLoading(true);
      const params = buildDomainParams(search, topic, rGte, tGte, trGte, adrGte, atrGte, ardGte);

      const response = await api.get('link-building/domains/', { params });
      setDomains(response.data);
    } catch (error) {
      console.error('Error fetching domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async (endpoint, fallbackFilename, params = {}, setDownloading) => {
    setDownloading(true);
    setUploadMessage(null);

    try {
      const response = await api.get(endpoint, {
        params,
        responseType: 'blob',
      });
      const fileName = getDownloadFilename(response.headers['content-disposition'], fallbackFilename);
      triggerBlobDownload(response.data, fileName);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      setUploadMessage('Error downloading Excel file.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadExcel(
      'link-building/domains/import_template/',
      'link_building_domain_template.xlsx',
      {},
      setDownloadingTemplate
    );
  };

  const handleDownloadExport = () => {
    const params = buildDomainParams(
      searchQuery,
      topicFilter,
      minRating,
      minTrust,
      minTraffic,
      minAhrefsDr,
      minAhrefsTraffic,
      minAhrefsRefdomains
    );
    downloadExcel('link-building/domains/export_excel/', 'link_building_domains.xlsx', params, setDownloadingExport);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('link-building/domains/upload_excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const ahrefsInfo = response.data.ahrefs
        ? ` | Ahrefs: ${response.data.ahrefs.updated}/${response.data.ahrefs.total} updated`
        : '';
      setUploadMessage(
        `Success! Created: ${response.data.domains_created}, Updated: ${response.data.domains_updated}${ahrefsInfo}`
      );
      fetchDomains(
        searchQuery,
        topicFilter,
        minRating,
        minTrust,
        minTraffic,
        minAhrefsDr,
        minAhrefsTraffic,
        minAhrefsRefdomains
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMessage(error.response?.data?.error || 'Error uploading file.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTopicFilter('');
    setMinRating('');
    setMinTrust('');
    setMinTraffic('');
    setMinAhrefsDr('');
    setMinAhrefsTraffic('');
    setMinAhrefsRefdomains('');
  };

  const handleRefreshAhrefs = async () => {
    setRefreshingAhrefs(true);
    try {
      const response = await api.post('link-building/domains/refresh_ahrefs/');
      setUploadMessage(`Ahrefs refresh: ${response.data.updated}/${response.data.total} updated`);
      fetchDomains(
        searchQuery,
        topicFilter,
        minRating,
        minTrust,
        minTraffic,
        minAhrefsDr,
        minAhrefsTraffic,
        minAhrefsRefdomains
      );
    } catch (error) {
      console.error('Error refreshing Ahrefs:', error);
      setUploadMessage('Error refreshing Ahrefs metrics.');
    } finally {
      setRefreshingAhrefs(false);
    }
  };

  const handleRefreshSemrush = async () => {
    setRefreshingSemrush(true);
    try {
      const response = await api.post('link-building/domains/refresh_semrush/');
      setUploadMessage(`Semrush refresh queued for ${response.data.queued} domains. Reload shortly to see updates.`);
    } catch (error) {
      console.error('Error queueing Semrush refresh:', error);
      setUploadMessage('Error queueing Semrush metrics refresh.');
    } finally {
      setRefreshingSemrush(false);
    }
  };

  const handleDeleteDomain = async (id, name) => {
    if (window.confirm(`Sei sicuro di voler eliminare il dominio "${name}"? Questa operazione è irreversibile.`)) {
      try {
        await api.delete(`link-building/domains/${id}/`);
        setSelectedDomains((prev) => prev.filter((selectedId) => selectedId !== id));
        fetchDomains(
          searchQuery,
          topicFilter,
          minRating,
          minTrust,
          minTraffic,
          minAhrefsDr,
          minAhrefsTraffic,
          minAhrefsRefdomains
        );
      } catch (error) {
        console.error('Error deleting domain:', error);
        alert(error.response?.data?.error || "Errore durante l'eliminazione del dominio.");
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDomains.length === 0) return;

    if (
      window.confirm(
        `Sei sicuro di voler eliminare i ${selectedDomains.length} domini selezionati? Questa operazione è irreversibile.`
      )
    ) {
      try {
        await api.post('link-building/domains/bulk_delete/', { ids: selectedDomains });
        setSelectedDomains([]);
        fetchDomains(
          searchQuery,
          topicFilter,
          minRating,
          minTrust,
          minTraffic,
          minAhrefsDr,
          minAhrefsTraffic,
          minAhrefsRefdomains
        );
      } catch (error) {
        console.error('Error deleting domains in bulk:', error);
        alert(error.response?.data?.error || "Errore durante l'eliminazione multipla.");
      }
    }
  };

  const getSortedSuppliers = (row) => {
    if (!row.suppliers || row.suppliers.length === 0) return '-';
    return [...row.suppliers].sort((a, b) => {
      const priceA = parseFloat(a.price);
      const priceB = parseFloat(b.price);
      if (isNaN(priceA) && isNaN(priceB)) return 0;
      if (isNaN(priceA)) return 1;
      if (isNaN(priceB)) return -1;
      return priceA - priceB;
    });
  };

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '-';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    }).format(value);
  };

  const priceFormatter = (cell, row) => {
    const suppliers = getSortedSuppliers(row);
    if (suppliers === '-') return '-';

    const validPrices = suppliers.map((supplierRow) => parseFloat(supplierRow.price)).filter((price) => !isNaN(price));
    const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : null;

    return (
      <div className="link-building-price-list">
        {suppliers.map((supplierRow) => {
          const price = parseFloat(supplierRow.price);
          const isLowest = minPrice != null && price === minPrice;
          return (
            <div key={supplierRow.id} className={isLowest ? 'font-weight-bold text-success' : ''}>
              {formatCurrency(price)}
              <span className="text-muted ml-1">({supplierRow.supplier?.name || '-'})</span>
            </div>
          );
        })}
      </div>
    );
  };

  const vendorFormatter = (cell, row) => {
    const suppliers = getSortedSuppliers(row);
    if (suppliers === '-') return '-';

    return (
      <div className="link-building-vendor-list">
        {suppliers.map((supplierRow) => (
          <div key={supplierRow.id}>{supplierRow.supplier?.name || '-'}</div>
        ))}
      </div>
    );
  };

  const numberFormatter = (cell) => {
    if (cell == null) return '-';
    return Number(cell).toLocaleString('it-IT');
  };

  const actionFormatter = (cell, row) => (
    <Button color="danger" size="sm" outline onClick={() => handleDeleteDomain(row.id, row.name)} title="Elimina">
      <i className="fas fa-trash" />
    </Button>
  );

  const columns = [
    { dataField: 'name', text: 'Domain', sort: true },
    { dataField: 'categoria', text: 'Categoria', sort: true },
    { dataField: 'topic', text: 'Topic', sort: true },
    { dataField: 'regione', text: 'Regione', sort: true },
    { dataField: 'vendor', isDummyField: true, text: 'Vendor', formatter: vendorFormatter },
    { dataField: 'price', isDummyField: true, text: 'Prezzo', formatter: priceFormatter },
    { dataField: 'rating', text: 'Rating', sort: true },
    { dataField: 'trust', text: 'Trust', sort: true },
    { dataField: 'ip', text: 'IP', sort: true },
    { dataField: 'citation', text: 'Citation', sort: true },
    { dataField: 'traffic', text: 'Traffic (Semrush)', sort: true, formatter: numberFormatter },
    { dataField: 'ahrefs_dr', text: 'DR (Ahrefs)', sort: true },
    { dataField: 'ahrefs_org_traffic', text: 'Traffic (Ahrefs)', sort: true, formatter: numberFormatter },
    { dataField: 'ahrefs_refdomains', text: 'Ref. Domains', sort: true, formatter: numberFormatter },
    { dataField: 'ahrefs_backlinks', text: 'Backlinks', sort: true, formatter: numberFormatter },
    { dataField: 'ahrefs_org_keywords', text: 'Keywords', sort: true, formatter: numberFormatter },
    {
      dataField: 'actions',
      text: 'Azioni',
      isDummyField: true,
      formatter: actionFormatter,
      headerStyle: { width: '80px', textAlign: 'center' },
      style: { textAlign: 'center' },
    },
  ];

  const selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    selected: selectedDomains,
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedDomains((prev) => [...prev, row.id]);
      } else {
        setSelectedDomains((prev) => prev.filter((value) => value !== row.id));
      }
    },
    onSelectAll: (isSelect, rows) => {
      const ids = rows.map((row) => row.id);
      if (isSelect) {
        setSelectedDomains((prev) => [...new Set([...prev, ...ids])]);
      } else {
        setSelectedDomains((prev) => prev.filter((value) => !ids.includes(value)));
      }
    },
  };

  return (
    <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
      <div className="container-fluid">
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap">
                <h3 className="mb-0">Link Building External Database</h3>
                <div className="d-flex align-items-center flex-wrap mt-3 mt-md-0">
                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative">
                      <InputGroupText>
                        <i className="fas fa-search" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Search domain..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative">
                      <InputGroupText>
                        <i className="fas fa-filter" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Topic..."
                        value={topicFilter}
                        onChange={(e) => setTopicFilter(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                      <InputGroupText>Rating ≥</InputGroupText>
                      <Input
                        type="number"
                        placeholder="0"
                        value={minRating}
                        onChange={(e) => setMinRating(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                      <InputGroupText>Trust ≥</InputGroupText>
                      <Input
                        type="number"
                        placeholder="0"
                        value={minTrust}
                        onChange={(e) => setMinTrust(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                      <InputGroupText>Traffic ≥</InputGroupText>
                      <Input
                        type="number"
                        placeholder="0"
                        value={minTraffic}
                        onChange={(e) => setMinTraffic(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative" style={{ width: '130px' }}>
                      <InputGroupText>DR ≥</InputGroupText>
                      <Input
                        type="number"
                        placeholder="0"
                        value={minAhrefsDr}
                        onChange={(e) => setMinAhrefsDr(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative" style={{ width: '170px' }}>
                      <InputGroupText>Ahrefs Traffic ≥</InputGroupText>
                      <Input
                        type="number"
                        placeholder="0"
                        value={minAhrefsTraffic}
                        onChange={(e) => setMinAhrefsTraffic(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <div className="mr-3 mb-2">
                    <InputGroup size="sm" className="input-group-alternative" style={{ width: '160px' }}>
                      <InputGroupText>Ref. Domains ≥</InputGroupText>
                      <Input
                        type="number"
                        placeholder="0"
                        value={minAhrefsRefdomains}
                        onChange={(e) => setMinAhrefsRefdomains(e.target.value)}
                      />
                    </InputGroup>
                  </div>

                  <Button
                    color="secondary"
                    size="sm"
                    onClick={clearFilters}
                    className="mr-3 mb-2"
                    style={{ height: '31px', padding: '0 10px' }}
                    title="Clear Filters"
                  >
                    <i className="fas fa-times" />
                  </Button>

                  {selectedDomains.length > 0 && (
                    <Button
                      color="danger"
                      size="sm"
                      onClick={handleBulkDelete}
                      className="mr-3 mb-2"
                      style={{ height: '31px', padding: '0 15px' }}
                    >
                      <i className="fas fa-trash mr-2" /> Elimina Selezionati ({selectedDomains.length})
                    </Button>
                  )}

                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />

                  <Button
                    color="secondary"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    style={{ height: '31px', padding: '0 15px' }}
                    className="mr-2 mb-2"
                    title="Scarica template Excel"
                  >
                    <i className="fas fa-file-excel mr-2" />
                    {downloadingTemplate ? 'Downloading...' : 'Template'}
                  </Button>

                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    style={{ height: '31px', padding: '0 15px' }}
                    className="mr-2 mb-2"
                  >
                    <i className="fas fa-upload mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Excel'}
                  </Button>

                  <Button
                    color="success"
                    size="sm"
                    onClick={handleDownloadExport}
                    disabled={downloadingExport}
                    style={{ height: '31px', padding: '0 15px' }}
                    className="mr-2 mb-2"
                    title="Scarica domini filtrati"
                  >
                    <i className="fas fa-file-download mr-2" />
                    {downloadingExport ? 'Downloading...' : 'Export domini'}
                  </Button>

                  <Button
                    color="warning"
                    size="sm"
                    onClick={handleRefreshAhrefs}
                    disabled={refreshingAhrefs}
                    style={{ height: '31px', padding: '0 15px' }}
                    className="mb-2"
                    title="Refresh Ahrefs metrics for all domains"
                  >
                    <i className="fas fa-sync-alt mr-2" />
                    {refreshingAhrefs ? 'Refreshing...' : 'Refresh Ahrefs'}
                  </Button>

                  <Button
                    color="info"
                    size="sm"
                    onClick={handleRefreshSemrush}
                    disabled={refreshingSemrush}
                    style={{ height: '31px', padding: '0 15px' }}
                    className="ml-2 mb-2"
                    title="Queue Semrush metrics refresh for all domains"
                  >
                    <i className="fas fa-wave-square mr-2" />
                    {refreshingSemrush ? 'Queueing...' : 'Refresh Semrush'}
                  </Button>

                  {uploadMessage && <span className="ml-2 text-sm text-success">{uploadMessage}</span>}
                </div>
              </CardHeader>
              <CardBody>
                <div
                  className="mb-4 text-muted text-sm px-3 py-2 border-left border-info bg-secondary"
                  style={{ borderWidth: '4px !important' }}
                >
                  <i className="fas fa-info-circle mr-2 text-info" />
                  <strong>Istruzioni Excel:</strong> Caricare un file <code>.xlsx</code> o <code>.xls</code>. <br />
                  <span className="ml-4">
                    <strong>Colonna richiesta:</strong> <code>domain</code>.
                  </span>
                  <br />
                  <span className="ml-4">
                    <strong>Colonne opzionali:</strong> <code>categoria</code>, <code>vendor</code>,{' '}
                    <code>prezzo</code>, <code>topic</code>, <code>regione</code>, <code>note</code>.
                  </span>
                  <br />
                  <span className="ml-4">Le metriche Ahrefs vengono scaricate automaticamente al caricamento.</span>
                  <br />
                  <span className="ml-4">
                    Le metriche Semrush vengono aggiornate in background: usa il refresh manuale e ricarica dopo poco.
                  </span>
                </div>
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="link-building-table">
                    <BootstrapTable
                      bootstrap4
                      keyField="id"
                      data={domains}
                      columns={columns}
                      selectRow={selectRow}
                      wrapperClasses="table-responsive link-building-table-scroll"
                      pagination={paginationFactory(getPaginationOptions())}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DomainList;
